/* eslint-disable react-hooks/set-state-in-effect */
import { useCallback, useEffect, useId, useMemo, useState, useContext } from 'react'
import DownloadRoundedIcon from '@mui/icons-material/DownloadRounded'
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded'
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import type { ChartPlacement, ChartSpec } from '../../../types/chart.js'
import { getChart } from '../../../services/chartService.js'
import { useChart } from '../../../hooks/useChart.js'
import { explainChart } from '../../../services/chartAiService.js'
import { exportChartToCsv, exportChartToPng } from './exportChart.js'
import { ChartEmbed, type ChartEmbedStatus } from './ChartEmbed.js'
import { Chart } from './Chart.js'
import { ActionMenu, type ActionMenuItem } from '../ActionMenu.js'
import { AppDialog } from '../AppDialog.js'
import { WorkspaceContext } from '../../../context/workspaceContextCore.js'
import { useNls } from '../../../nls/nlsProvider.js'
import { useIsMemberMobile } from '../../../member/useIsMemberMobile.js'
import { getWrapperRole } from '../../../utils/wrapperRole.js'
import { ChartBuilderDialog } from './ChartBuilderDialog.js'
import { RecordListPanel, type ChartRecord } from './RecordListPanel.js'
import { SendToChatMenu } from '../../Analysis/popover/panels/SendToChatMenu.js'
import './chart.css'

const HEIGHT_PX_MAP: Record<'S' | 'M' | 'L', number> = {
  S: 180,
  M: 260,
  L: 400,
}

export interface ChartPlacementEmbedProps {
  placement: ChartPlacement
  workspaceId: string
  className?: string
  testId?: string
  onEdit?: (spec: ChartSpec) => void
  onOpenInCharts?: (chartId: string) => void
  onSendToChat?: (chartId: string) => void
}

export function ChartPlacementEmbed({
  placement,
  workspaceId,
  className = '',
  testId,
  onEdit,
  onOpenInCharts,
  onSendToChat,
}: ChartPlacementEmbedProps) {
  const { t } = useNls()
  const isMemberMobile = useIsMemberMobile()
  const wrapperRole = getWrapperRole()
  const isPhone = isMemberMobile || wrapperRole === 'member' || wrapperRole === 'helper'
  const [phoneSheetOpen, setPhoneSheetOpen] = useState(false)
  const [sendToChatOpen, setSendToChatOpen] = useState(false)
  const workspaceContext = useContext(WorkspaceContext)
  const currentWorkspace = workspaceContext?.workspaces.find((w) => w.id === (workspaceContext.activeWorkspaceId || workspaceId))
  const canManage = !isPhone && workspaceContext ? workspaceContext.canPerform('chart.manage') : false
  const containerId = useId().replace(/:/g, '')

  const [spec, setSpec] = useState<ChartSpec | null>(null)
  const [loadingSpec, setLoadingSpec] = useState(true)
  const [specNotFound, setSpecNotFound] = useState(false)
  const [specError, setSpecError] = useState<Error | null>(null)

  const [isEditing, setIsEditing] = useState(false)
  const [explanationText, setExplanationText] = useState<string | null>(null)
  const [explaining, setExplaining] = useState(false)
  const [explanationError, setExplanationError] = useState<string | null>(null)

  const loadSpec = useCallback(async () => {
    setLoadingSpec(true)
    setSpecNotFound(false)
    setSpecError(null)
    try {
      const loaded = await getChart(workspaceId, placement.chartId)
      if (!loaded) {
        setSpecNotFound(true)
        setSpec(null)
      } else {
        setSpec(loaded)
      }
    } catch (err: unknown) {
      setSpecError(err instanceof Error ? err : new Error(String(err)))
    } finally {
      setLoadingSpec(false)
    }
  }, [workspaceId, placement.chartId])

  useEffect(() => {
    void loadSpec()
  }, [loadSpec])

  const rangeOverride = placement.overrides?.range
  const effectiveSpec = useMemo(() => {
    if (!spec) return null
    if (!rangeOverride) return spec
    return {
      ...spec,
      range: rangeOverride,
    }
  }, [spec, rangeOverride])

  const {
    result,
    loading: queryLoading,
    error: queryError,
    refresh,
  } = useChart({
    workspaceId,
    chartId: placement.chartId,
    spec: effectiveSpec ?? undefined,
    enabled: Boolean(effectiveSpec),
  })

  const height = placement.overrides?.height ? HEIGHT_PX_MAP[placement.overrides.height] : 260

  const handleRefresh = useCallback(() => {
    if (specNotFound) {
      void loadSpec()
    } else {
      void refresh()
    }
  }, [specNotFound, loadSpec, refresh])

  const handleOpenInCharts = useCallback(() => {
    if (onOpenInCharts) {
      onOpenInCharts(placement.chartId)
      return
    }
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href)
      url.searchParams.set('tab', 'CHARTS')
      url.searchParams.set('chartId', placement.chartId)
      window.location.assign(url.toString())
    }
  }, [onOpenInCharts, placement.chartId])

  const handleExportCsv = useCallback(() => {
    if (effectiveSpec && result) {
      exportChartToCsv(result, `${effectiveSpec.title}.csv`)
    }
  }, [effectiveSpec, result])

  const handleExportPng = useCallback(() => {
    if (!effectiveSpec) return
    const container = document.getElementById(`chart-placement-${containerId}`)
    const svg = container?.querySelector('svg')
    if (svg) {
      void exportChartToPng(svg, `${effectiveSpec.title}.png`)
    }
  }, [containerId, effectiveSpec])

  const handleExplain = useCallback(async () => {
    if (!effectiveSpec || !result) return
    setExplaining(true)
    setExplanationError(null)
    setExplanationText(null)
    try {
      // CC-P9 sanitized: sends computed series only, never record samples or titles
      const res = await explainChart(workspaceId, effectiveSpec, result.series)
      if (res.ok) {
        setExplanationText(res.text)
      } else {
        setExplanationError(res.reason || t('NLSChartsCantChartYet'))
      }
    } catch (err: unknown) {
      const errObj = err as { message?: string }
      setExplanationError(errObj?.message || String(err))
    } finally {
      setExplaining(false)
    }
  }, [effectiveSpec, result, workspaceId, t])

  const handleEdit = useCallback(() => {
    if (onEdit && effectiveSpec) {
      onEdit(effectiveSpec)
    } else {
      setIsEditing(true)
    }
  }, [effectiveSpec, onEdit])

  // Map ChartResult.records to ChartRecord[] for RecordListPanel
  const recordSample = result?.records?.sample
  const chartRecords: ChartRecord[] = useMemo(() => {
    if (!recordSample) return []
    return recordSample.map((sample) => ({
      id: sample.id,
      primary: sample.title,
      drillTarget: null,
    }))
  }, [recordSample])

  // Determine status
  let status: ChartEmbedStatus = 'ready'
  let errorMessage: string | undefined

  const specErrCode = (specError as { code?: string } | null)?.code
  const queryErrCode = (queryError as { code?: string } | null)?.code

  if (loadingSpec || (queryLoading && !result)) {
    status = 'loading'
  } else if (specNotFound) {
    status = 'deleted'
  } else if (
    specError?.message?.includes('permission') ||
    specErrCode === 'permission-denied' ||
    queryError?.message?.includes('permission') ||
    queryErrCode === 'permission-denied'
  ) {
    status = 'no-access'
    errorMessage = t('NLSChartsNoAccess')
  } else if (specError || queryError) {
    status = 'error'
    errorMessage = specError?.message || queryError?.message || t('NLSChartStateError')
  } else if (!result || !result.series || result.series.length === 0 || result.series.every((s) => !s.points || s.points.length === 0)) {
    status = 'empty'
  }

  const menuItems: ActionMenuItem[] = useMemo(() => {
    const items: ActionMenuItem[] = [
      {
        key: 'open',
        label: t('NLSChartsOpenInCharts'),
        icon: <OpenInNewRoundedIcon fontSize="small" />,
        onSelect: handleOpenInCharts,
      },
      {
        key: 'csv',
        label: t('NLSChartsDownloadCsv'),
        icon: <DownloadRoundedIcon fontSize="small" />,
        disabled: !result || status !== 'ready',
        onSelect: handleExportCsv,
      },
      {
        key: 'png',
        label: t('NLSChartsDownloadPng'),
        icon: <DownloadRoundedIcon fontSize="small" />,
        disabled: !result || status !== 'ready',
        onSelect: handleExportPng,
      },
      {
        key: 'explain',
        label: t('NLSChartsExplain'),
        icon: <AutoAwesomeRoundedIcon fontSize="small" />,
        disabled: !result || status !== 'ready',
        onSelect: () => void handleExplain(),
      },
      {
        key: 'chat',
        label: t('NLSChartsSendToChat'),
        icon: <SendRoundedIcon fontSize="small" />,
        disabled: !result || status !== 'ready',
        onSelect: () => {
          if (onSendToChat) {
            onSendToChat(placement.chartId)
          } else {
            setSendToChatOpen(true)
          }
        },
      },
    ]

    if (canManage) {
      items.push({
        key: 'edit',
        label: t('NLSChartsEdit'),
        icon: <EditRoundedIcon fontSize="small" />,
        dividerBefore: true,
        onSelect: handleEdit,
      })
    }

    return items
  }, [
    t,
    handleOpenInCharts,
    result,
    status,
    handleExportCsv,
    handleExportPng,
    handleExplain,
    canManage,
    handleEdit,
    placement.chartId,
    onSendToChat,
  ])

  return (
    <div
      className={`chartPlacementEmbed ${className}`}
      data-testid={testId}
      id={`chart-placement-${containerId}`}
      onClick={isPhone ? () => setPhoneSheetOpen(true) : undefined}
      style={isPhone ? { cursor: 'pointer' } : undefined}
      role={isPhone ? 'button' : undefined}
      tabIndex={isPhone ? 0 : undefined}
    >
      {!isPhone && (
        <div className="chartPlacementHeader">
          <div className="chartPlacementMenu">
            <ActionMenu
              items={menuItems}
              trigger="kebab"
              ariaLabel={t('NLSChartsOpenInCharts')}
            />
          </div>
        </div>
      )}

      <ChartEmbed
        status={status}
        errorMessage={errorMessage}
        onRefresh={handleRefresh}
        onExportCsv={status === 'ready' ? handleExportCsv : undefined}
        onExportPng={status === 'ready' ? handleExportPng : undefined}
        testId={testId ? `${testId}-embed` : undefined}
      >
        {effectiveSpec && result && (
          <Chart
            spec={effectiveSpec}
            data={result}
            height={height}
            testId={testId ? `${testId}-chart` : undefined}
          />
        )}
      </ChartEmbed>

      {/* Full-screen sheet on phone */}
      {isPhone && phoneSheetOpen && effectiveSpec && result && (
        <AppDialog
          open={phoneSheetOpen}
          title={effectiveSpec.title || t('NLSChartPhoneFallbackTitle')}
          onDismiss={() => setPhoneSheetOpen(false)}
          size="full"
          data-testid="chart-phone-fullscreen-sheet"
          actions={[
            {
              label: t('NLSChartActionExportCsv'),
              variant: 'secondary',
              onClick: handleExportCsv,
            },
            {
              label: t('NLSCommonActionClose'),
              variant: 'primary',
              onClick: () => setPhoneSheetOpen(false),
            },
          ]}
        >
          <div className="chartPhoneSheetContent">
            <div className="chartPhoneSheetChartContainer">
              <Chart
                spec={effectiveSpec}
                data={result}
                height={280}
                testId={`${testId ?? 'phone'}-sheet-chart`}
              />
            </div>
            <div className="chartPhoneSheetRecordList">
              <RecordListPanel records={chartRecords} loading={queryLoading} />
            </div>
            <div className="chartPhoneSheetRecords">
              <h4 className="chartPhoneSheetRecordsTitle">
                {t('NLSChartA11yDataTable', { title: effectiveSpec.title || t('NLSChartPhoneFallbackData') })}
              </h4>
              <table className="chartRecordsTable">
                <thead>
                  <tr className="chartRecordsTableHeader">
                    <th className="chartRecordsTableCell">{t('NLSChartPhoneTableSeries')}</th>
                    <th className="chartRecordsTableCell">{t('NLSChartPhoneTableX')}</th>
                    <th className="chartRecordsTableCellNum">{t('NLSChartPhoneTableY')}</th>
                  </tr>
                </thead>
                <tbody>
                  {result.series.flatMap((s, sIdx) =>
                    s.points.map((p, idx) => (
                      <tr key={`${s.key ?? sIdx}-${idx}`} className="chartRecordsTableRow">
                        <td className="chartRecordsTableCell">{s.label}</td>
                        <td className="chartRecordsTableCell">{String(p.x)}</td>
                        <td className="chartRecordsTableCellNum">{String(p.y)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </AppDialog>
      )}

      {/* Explanation dialog */}
      <AppDialog
        open={explaining || Boolean(explanationText) || Boolean(explanationError)}
        title={t('NLSChartsExplainTitle')}
        onDismiss={() => {
          setExplanationText(null)
          setExplanationError(null)
        }}
        size="md"
        actions={[
          {
            label: t('NLSChartsCancel'),
            variant: 'secondary',
            onClick: () => {
              setExplanationText(null)
              setExplanationError(null)
            },
          },
        ]}
      >
        <div className="chartExplanationContent" data-testid="chart-explanation-modal">
          {explaining ? (
            <p>{t('NLSChartsExplaining')}</p>
          ) : explanationError ? (
            <p className="chartExplanationError">{explanationError}</p>
          ) : (
            <p className="chartExplanationBody">{explanationText}</p>
          )}
        </div>
      </AppDialog>

      {/* Builder dialog if opened from Edit */}
      {isEditing && effectiveSpec && (
        <ChartBuilderDialog
          open={isEditing}
          workspaceId={workspaceId}
          initialSpec={effectiveSpec}
          onClose={() => setIsEditing(false)}
          onSaved={(_id, updatedSpec) => {
            setSpec(updatedSpec)
            setIsEditing(false)
            void refresh()
          }}
        />
      )}

      {/* Send to chat dialog */}
      {sendToChatOpen && effectiveSpec && (
        <AppDialog
          open={sendToChatOpen}
          title={t('NLSChartsSendToChatDialogTitle')}
          onDismiss={() => setSendToChatOpen(false)}
          size="sm"
          actions={[
            {
              label: t('NLSCommonActionCancel'),
              variant: 'secondary',
              onClick: () => setSendToChatOpen(false),
            },
          ]}
        >
          <div className="chartSendToChatDialogContent" style={{ padding: '16px 0' }}>
            <SendToChatMenu
              workspace={currentWorkspace}
              chartId={placement.chartId}
              chartTitle={effectiveSpec.title}
              getSvg={() => document.getElementById(`chart-placement-${containerId}`)?.querySelector('svg') ?? null}
              onSent={() => setSendToChatOpen(false)}
            />
          </div>
        </AppDialog>
      )}
    </div>
  )
}
